<template>
  <div class="d-flex gap-3" style="height: calc(100vh - 14rem);">
    <!-- Sidebar -->
    <div v-if="showSidebar" class="border rounded p-3 flex-shrink-0 d-flex flex-column" style="width: 250px;">
      <h6 class="mb-2">Messdiener:innen</h6>
      <div class="d-flex gap-1 mb-2">
        <button class="btn btn-sm btn-outline-secondary" type="button" @click="selectAll">Alle</button>
        <button class="btn btn-sm btn-outline-secondary" type="button" @click="deselectAll">Keine</button>
      </div>
      <div class="flex-grow-1 overflow-auto" style="min-height: 0;">
        <multiselect v-model="selectedServerIds"
                     :close-on-select="false"
                     :custom-label="getPlanname"
                     :multiple="true"
                     :options="servers.map(s => s.id)">
        </multiselect>
      </div>
      <button v-if="!readonly" class="btn btn-success btn-sm mt-2" type="button" @click="openCreateModalManual">
        <em class="material-icons align-middle" style="font-size: 1rem;">add</em>
        Abmeldung hinzufügen
      </button>
    </div>

    <!-- Calendar -->
    <div class="flex-grow-1 d-flex flex-column" style="min-width: 0;">
      <!-- Navigation -->
      <div class="d-flex align-items-center justify-content-between mb-2">
        <button class="btn btn-outline-secondary btn-sm" type="button" @click="prevMonth">
          <em class="material-icons align-middle">chevron_left</em>
        </button>
        <h5 class="mb-0">{{ monthName }} {{ currentYear }}</h5>
        <button class="btn btn-outline-secondary btn-sm" type="button" @click="nextMonth">
          <em class="material-icons align-middle">chevron_right</em>
        </button>
      </div>

      <div v-if="showNoServerWarning" class="alert alert-warning py-2 mb-2" role="alert">
        Bitte Messdiener:innen in der Seitenleiste auswählen.
      </div>

      <div v-if="!showSidebar && !readonly" class="mb-2 text-end">
        <button class="btn btn-success btn-sm" type="button" @click="openCreateModalManual">
          <em class="material-icons align-middle" style="font-size: 1rem;">add</em>
          Abmeldung hinzufügen
        </button>
      </div>

      <!-- Calendar grid -->
      <div ref="calendarGrid" class="calendar-grid flex-grow-1" @mouseup="onCalendarMouseup" @contextmenu.prevent>
        <!-- Day headers -->
        <div v-for="d in ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']" :key="d" class="cal-header">{{ d }}</div>

        <!-- Day cells -->
        <div v-for="(day, idx) in calendarDays" :key="idx"
             :class="['cal-cell', { 'other-month': !day.inMonth, 'out-of-period': day.inMonth && !isInPeriod(day), 'in-selection': isDayInSelection(day), 'today': isToday(day) }]"
             :data-date="formatDate(day.date)"
             @mousedown.left.prevent="onDayMousedown(day)"
             @mousemove="onDayMousemove(day)"
             @touchstart="onDayTouchStart($event, day)">
          <span class="cal-day-number">{{ day.day }}</span>
          <div class="cal-blocks">
            <div v-for="absence in getAbsencesForDay(day)"
                 :key="absence.id"
                 :class="['cal-block', 'text-white', 'text-truncate']"
                 :style="{backgroundColor: getColor(absence.altarServer)}"
                 :title="getPlanname(absence.altarServer) + ': ' + formatDateDisplay(absence.startDate) + ' – ' + formatDateDisplay(absence.endDate)"
                 @mousedown.stop
                 @click.stop="openEditModal(absence)"
                 @touchstart.stop.passive>
              {{ getPlanname(absence.altarServer) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Create / Edit modal -->
  <teleport to="body">
    <div ref="createModal" class="modal fade" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditMode ? 'Abmeldung bearbeiten' : 'Abmeldung erstellen' }}</h5>
            <button type="button" class="btn-close" @click="closeCreateModal"></button>
          </div>
          <div class="modal-body" v-if="pendingModal">
            <div class="row g-3 mb-3">
              <div class="col">
                <label class="form-label" for="createStartDate">Start-Datum</label>
                <input id="createStartDate" v-model="pendingModal.startDate" class="form-control" type="date" required
                       :min="minDate || undefined" :max="maxDate || undefined"
                       @blur="pendingModal.startDate = clampDateStr(pendingModal.startDate)">
              </div>
              <div class="col">
                <label class="form-label" for="createEndDate">End-Datum</label>
                <input id="createEndDate" v-model="pendingModal.endDate" class="form-control" type="date" required
                       :min="minDate || undefined" :max="maxDate || undefined"
                       @blur="pendingModal.endDate = clampDateStr(pendingModal.endDate)">
              </div>
            </div>
            <template v-if="isEditMode">
              <p class="mb-0">
                <strong>Messdiener:in:</strong> {{ getPlanname(pendingModal.altarServer) }}
              </p>
              <p v-if="isCrossingAbsence" class="text-muted small mb-0 mt-2">
                Nur der bearbeitbare Zeitraum wird gelöscht.
              </p>
            </template>
            <template v-else-if="showSidebar">
              <p class="mb-1"><strong>Messdiener:innen:</strong></p>
              <ul class="mb-0">
                <li v-for="id in selectedServerIds" :key="id">{{ getPlanname(id) }}</li>
              </ul>
              <p v-if="selectedServerIds.length === 0" class="text-muted fst-italic mb-0">
                Keine Messdiener:innen ausgewählt.
              </p>
            </template>
            <template v-else>
              <label class="form-label"><strong>Für wen?</strong></label>
              <multiselect v-model="modalServerIds"
                           :close-on-select="false"
                           :custom-label="getPlanname"
                           :multiple="true"
                           :options="servers.map(s => s.id)">
              </multiselect>
            </template>
          </div>
          <div class="modal-footer">
            <button v-if="isEditMode" class="btn btn-danger me-auto" type="button" @click="confirmDelete">Löschen</button>
            <button class="btn btn-secondary" type="button" @click="closeCreateModal">Abbrechen</button>
            <button v-if="!isEditMode" class="btn btn-success" type="button"
                    :disabled="!pendingModal || !pendingModal.startDate || !pendingModal.endDate || createServerIds.length === 0"
                    @click="confirmCreate">Erstellen</button>
            <button v-else class="btn btn-primary" type="button"
                    :disabled="!pendingModal || !pendingModal.startDate || !pendingModal.endDate"
                    @click="confirmEdit">Speichern</button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import {Modal} from 'bootstrap';
import Multiselect from "vue-multiselect";

const PALETTE = [
  '#4A90D9', '#E67E22', '#2ECC71', '#9B59B6', '#E74C3C',
  '#1ABC9C', '#F39C12', '#3498DB', '#27AE60', '#8E44AD',
  '#F1C40F', '#16A085', '#D35400', '#2980B9', '#C0392B',
  '#7D3C98', '#117A65', '#B7950B', '#1A5276', '#884EA0',
];

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

export default {
  name: 'AbsenceCalendar',
  components: {Multiselect},
  props: {
    servers:     {type: Array,   required: true},  // [{id, planname}]
    absences:    {type: Array,   required: true},
    showSidebar: {type: Boolean, default: true},
    readonly:    {type: Boolean, default: false},
    minDate:     {type: String,  default: null},   // ISO date "YYYY-MM-DD", inclusive
    maxDate:     {type: String,  default: null},   // ISO date "YYYY-MM-DD", inclusive
  },
  emits: ['create', 'update', 'delete'],
  data() {
    const now = new Date();
    return {
      currentYear: now.getFullYear(),
      currentMonth: now.getMonth(),
      selectedServerIds: [],
      modalServerIds: [],
      dragStartDate: null,
      dragEndDate: null,
      isDragging: false,
      pendingModal: null,
      isEditMode: false,
      isCrossingAbsence: false,
      showNoServerWarning: false,
      globalMouseupHandler: null,
    };
  },
  computed: {
    monthName() {
      return MONTH_NAMES[this.currentMonth];
    },
    calendarDays() {
      const year = this.currentYear;
      const month = this.currentMonth;
      const firstDay = new Date(year, month, 1);
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const startDow = (firstDay.getDay() + 6) % 7; // Mon=0 … Sun=6

      const days = [];

      for (let i = startDow - 1; i >= 0; i--) {
        const d = new Date(year, month, -i);
        days.push({date: d, inMonth: false, day: d.getDate()});
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month, d);
        days.push({date, inMonth: true, day: d});
      }
      // Fill trailing next-month days to always reach exactly 42 cells (6 full weeks)
      let nextDay = 1;
      while (days.length < 42) {
        const d = new Date(year, month + 1, nextDay++);
        days.push({date: d, inMonth: false, day: d.getDate()});
      }

      return days;
    },
    selectionRange() {
      if (!this.dragStartDate || !this.dragEndDate) return null;
      const a = this.dragStartDate;
      const b = this.dragEndDate;
      return {start: a <= b ? a : b, end: a <= b ? b : a};
    },
    createServerIds() {
      return this.showSidebar ? this.selectedServerIds : this.modalServerIds;
    },
  },
  watch: {
    servers: {
      immediate: true,
      handler(newVal) {
        if (!this.showSidebar || this.selectedServerIds.length === 0) {
          this.selectedServerIds = newVal.map(s => s.id);
        }
      },
    },
    minDate(newVal, oldVal) {
      if (!oldVal && newVal) {
        const d = new Date(newVal + 'T00:00:00');
        this.currentYear = d.getFullYear();
        this.currentMonth = d.getMonth();
      }
    },
  },
  methods: {
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentYear--;
        this.currentMonth = 11;
      } else {
        this.currentMonth--;
      }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentYear++;
        this.currentMonth = 0;
      } else {
        this.currentMonth++;
      }
    },
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    formatDateDisplay(dateStr) {
      const [y, m, d] = dateStr.split('-');
      return `${d}.${m}.${y}`;
    },
    getColor(altarServerId) {
      const idx = this.servers.findIndex(s => s.id === altarServerId);
      return idx >= 0 ? PALETTE[idx % PALETTE.length] : '#6c757d';
    },
    getPlanname(id) {
      const server = this.servers.find(s => s.id === id);
      return server ? server.planname : '?';
    },
    selectAll() {
      this.selectedServerIds = this.servers.map(s => s.id);
    },
    deselectAll() {
      this.selectedServerIds = [];
    },
    isInPeriod(day) {
      const s = this.formatDate(day.date);
      if (this.minDate && s < this.minDate) return false;
      if (this.maxDate && s > this.maxDate) return false;
      return true;
    },
    isToday(day) {
      const today = new Date();
      return day.date.getFullYear() === today.getFullYear() &&
        day.date.getMonth() === today.getMonth() &&
        day.date.getDate() === today.getDate();
    },
    getAbsencesForDay(day) {
      const dateStr = this.formatDate(day.date);
      return this.absences.filter(a =>
        this.selectedServerIds.includes(a.altarServer) &&
        a.startDate <= dateStr &&
        a.endDate >= dateStr
      );
    },
    isDayInSelection(day) {
      if (!this.selectionRange) return false;
      return day.date >= this.selectionRange.start && day.date <= this.selectionRange.end;
    },
    onDayMousedown(day) {
      if (!day.inMonth || this.readonly || !this.isInPeriod(day)) return;
      this.isDragging = true;
      this.dragStartDate = day.date;
      this.dragEndDate = day.date;
    },
    onDayMousemove(day) {
      if (!this.isDragging) return;
      this.dragEndDate = day.date;
    },
    clampDateStr(dateStr) {
      if (this.minDate && dateStr < this.minDate) return this.minDate;
      if (this.maxDate && dateStr > this.maxDate) return this.maxDate;
      return dateStr;
    },
    onCalendarMouseup() {
      if (!this.isDragging || !this.dragStartDate) return;
      const range = this.selectionRange;
      this.isDragging = false;
      this.dragStartDate = null;
      this.dragEndDate = null;

      if (this.showSidebar && this.selectedServerIds.length === 0) {
        this.showNoServerWarning = true;
        setTimeout(() => {
          this.showNoServerWarning = false;
        }, 3000);
        return;
      }

      this.isEditMode = false;
      this.modalServerIds = this.servers.map(s => s.id);
      this.pendingModal = {
        startDate: this.clampDateStr(this.formatDate(range.start)),
        endDate: this.clampDateStr(this.formatDate(range.end)),
      };
      this.createModalInstance.show();
    },
    openCreateModalManual() {
      if (this.readonly) return;
      this.isEditMode = false;
      this.modalServerIds = this.servers.map(s => s.id);
      this.pendingModal = {startDate: '', endDate: ''};
      this.createModalInstance.show();
    },
    closeCreateModal() {
      this.createModalInstance.hide();
    },
    confirmCreate() {
      this.$emit('create', {
        altarServers: this.createServerIds,
        startDate: this.pendingModal.startDate,
        endDate: this.pendingModal.endDate,
      });
      this.closeCreateModal();
    },
    isAbsenceInPeriod(absence) {
      if (this.minDate && absence.startDate < this.minDate) return false;
      if (this.maxDate && absence.endDate > this.maxDate) return false;
      return true;
    },
    absenceOverlapsPeriod(absence) {
      if (this.minDate && absence.endDate < this.minDate) return false;
      if (this.maxDate && absence.startDate > this.maxDate) return false;
      return true;
    },
    openEditModal(absence) {
      if (this.readonly || !this.absenceOverlapsPeriod(absence)) return;
      this.isEditMode = true;
      this.isCrossingAbsence = !this.isAbsenceInPeriod(absence);
      this.pendingModal = {
        ...absence,
        startDate: this.clampDateStr(absence.startDate),
        endDate: this.clampDateStr(absence.endDate),
      };
      this.createModalInstance.show();
    },
    confirmEdit() {
      this.$emit('update', {...this.pendingModal});
      this.closeCreateModal();
    },
    confirmDelete() {
      const absence = {...this.pendingModal};
      this.$emit('delete', absence);
      this.closeCreateModal();
    },
    onDayTouchStart(event, day) {
      if (!day.inMonth || this.readonly || !this.isInPeriod(day)) return;
      this._touchDragReady = true;
      this.dragStartDate = day.date;
      this.dragEndDate = day.date;
    },
  },
  mounted() {
    this.createModalInstance = new Modal(this.$refs.createModal);

    this.$refs.createModal.addEventListener('hide.bs.modal', () => {
      this.pendingModal = null;
      this.isEditMode = false;
      this.isCrossingAbsence = false;
      this.modalServerIds = [];
    });

    this.globalMouseupHandler = () => {
      if (this.isDragging) {
        this.isDragging = false;
        this.dragStartDate = null;
        this.dragEndDate = null;
      }
    };
    window.addEventListener('mouseup', this.globalMouseupHandler);

    this._touchDragReady = false;

    this.globalTouchMoveHandler = (event) => {
      if (!this._touchDragReady) return;
      event.preventDefault();
      if (!this.isDragging) this.isDragging = true;
      const touch = event.touches[0];
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      const cell = el?.closest('[data-date]');
      if (cell) {
        const match = this.calendarDays.find(d => this.formatDate(d.date) === cell.dataset.date);
        if (match) this.dragEndDate = match.date;
      }
    };
    window.addEventListener('touchmove', this.globalTouchMoveHandler, {passive: false});

    this.globalTouchEndHandler = () => {
      if (!this._touchDragReady) return;
      this._touchDragReady = false;
      if (this.isDragging) {
        this.onCalendarMouseup();
      } else {
        this.dragStartDate = null;
        this.dragEndDate = null;
      }
    };
    window.addEventListener('touchend', this.globalTouchEndHandler);
  },
  beforeUnmount() {
    window.removeEventListener('mouseup', this.globalMouseupHandler);
    window.removeEventListener('touchmove', this.globalTouchMoveHandler);
    window.removeEventListener('touchend', this.globalTouchEndHandler);
  },
};
</script>

<style scoped>
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: auto 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 1px;
  background-color: #dee2e6;
  border: 1px solid #dee2e6;
  user-select: none;
  min-height: 0;
}

.cal-header {
  background-color: #f8f9fa;
  text-align: center;
  padding: 4px 2px;
  font-weight: 600;
  font-size: 0.85rem;
  color: #495057;
}

.cal-cell {
  background-color: #fff;
  padding: 4px;
  cursor: default;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.cal-cell:hover {
  background-color: #f0f7ff;
}

.cal-cell.other-month {
  background-color: #f8f9fa;
  color: #adb5bd;
}

.cal-cell.out-of-period {
  background-color: #f8f9fa;
  color: #adb5bd;
  cursor: not-allowed;
}

.cal-cell.in-selection {
  background-color: #cfe2ff;
}

.cal-cell.today .cal-day-number {
  background-color: #0d6efd;
  color: #fff;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
}

.cal-day-number {
  font-size: 0.8rem;
  font-weight: 500;
  display: block;
  text-align: right;
  margin-bottom: 2px;
}

.cal-blocks {
  display: flex;
  flex-direction: column;
  gap: 1px;
  overflow-y: auto;
  min-height: 0;
  flex: 1;
}

.cal-block {
  font-size: 0.7rem;
  padding: 1px 3px;
  border-radius: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  flex-shrink: 0;
}

</style>
